/**
 * Portal Theme Composable
 *
 * Bridges clientportal portal branding to the shared theme system. Reads
 * `brandColor1` from the location store and calls `useTheme().setBrandTokens`
 * so the full CP theme — color scales (50-900), contrast colors, and the
 * HighRise `--primary-*` / `--error-*` bridge — is generated from the portal's
 * brand color.
 *
 * Mounted once in the persistent clientportal root shell (`CPRootContainer`).
 * Community group routes render inside the same app and own the theme via
 * `useCommunityTheme`, which calls `resetBrandTokens()` on unmount. So this
 * composable must RE-ASSERT the portal theme — not apply once — whenever:
 *   - the brand color resolves,
 *   - the route leaves a community back to a clientportal page.
 *
 * Active theme tokens are not a route-watcher source. During client-side
 * navigation the community layout can apply its tokens before Nuxt synchronizes
 * `useRoute` in this persistent root. Reacting to that output would let the stale
 * portal route immediately overwrite the community theme.
 *
 * A separate watcher reclaims the portal only when tokens are cleared. Desktop
 * `layoutTransition: out-in` can keep the community layout mounted through its
 * leave animation, so `useCommunityTheme` may call `resetBrandTokens()` after
 * this composable already restored the portal. Re-applying on `null` recovers
 * that delayed reset without overwriting a newly applied community theme.
 * `brandColor1` → primary and `brandColor2` → secondary (see `mapPortalBrandingToTokens`).
 */

import { watch } from 'vue'
import { useRoute } from 'nuxt/app'

import { useTheme } from '@common/theme-layer/composables/useTheme'
import type { BrandTokens } from '@common/theme-layer/composables/useTheme'

import { useLocationStore } from '../store/location.store'
import { resolvePortalBrandTokens } from '../utils/portal-brand-tokens'

const COMMUNITY_GROUP_ROUTE_PREFIX = 'communities-groups'
const COMMUNITY_GROUP_PATH_PATTERN = /^\/communities\/groups\/[^/]+/

export const usePortalTheme = () => {
  const { activeBrandTokens, resetBrandTokens, setBrandTokens } = useTheme()
  const locationStore = useLocationStore()
  const route = useRoute()

  const isCommunityGroupRoute = () => {
    const groupSlug = route.params.groupSlug
    // `groupSlug` is reserved for group-scoped routes, including GoKollab aliases
    // and courses nested under a community whose route name has a different prefix.
    const hasGroupSlug = typeof groupSlug === 'string' && groupSlug.trim().length > 0
    const hasCommunityPath = COMMUNITY_GROUP_PATH_PATTERN.test(route.path)
    const hasCommunityRouteName = typeof route.name === 'string' && route.name.startsWith(COMMUNITY_GROUP_ROUTE_PREFIX)

    return hasGroupSlug || hasCommunityPath || hasCommunityRouteName
  }

  const applyPortalTheme = () => {
    // Community routes own the theme via useCommunityTheme — don't fight it there.
    if (isCommunityGroupRoute()) {
      return
    }

    const tokens = resolvePortalBrandTokens(locationStore.branding)

    if (!tokens) {
      if (activeBrandTokens.value) {
        resetBrandTokens()
      }
      return
    }

    // Idempotent: skip when every token we would set is already active. Compare
    // secondary explicitly because it can be absent from Object.keys(tokens).
    const active = activeBrandTokens.value
    const alreadyActive =
      !!active &&
      active.secondary === tokens.secondary &&
      (Object.keys(tokens) as (keyof BrandTokens)[]).every(key => active[key] === tokens[key])

    if (alreadyActive) {
      return
    }

    setBrandTokens(tokens)
  }

  // Single-mount in the persistent CPRootContainer root: the watcher lives for the
  // app-session lifetime and Vue disposes it on root teardown, so no manual stop is
  // needed. Do not reuse this composable in a non-root/remountable context without
  // adding teardown.
  watch(
    () => [locationStore.branding.brandColor1, locationStore.branding.brandColor2, route.name, route.path, route.params.groupSlug],
    applyPortalTheme,
    // Community teardown runs during the render flush. Re-applying the portal
    // theme afterward prevents its reset hook from clearing the restored tokens.
    { immediate: true, flush: 'post' }
  )

  // Reclaim only after a reset (`null`). Non-null tokens belong to whoever just
  // applied them (usually a community layout whose route has not synced yet).
  watch(
    activeBrandTokens,
    tokens => {
      if (tokens) {
        return
      }

      applyPortalTheme()
    },
    { flush: 'post' }
  )

  return { applyPortalTheme }
}
